(ns frontend.state.current-session
  (:require
   [clojure.set :as set]
   [clojure.string :as string]
   [common.date-formats :as date-formats]
   [common.entity :as entity]
   [common.features.plan :as plan]
   [common.localization :as loc]
   [common.modules :as modules]
   [common.roles :as roles]
   [common.schema.assets :as assets]
   [common.schema.entities.pipeline :as pipeline]
   [common.schema.entity-types :refer [decision-entity-types]]
   [common.schema.registry :as registry]
   [common.sync-model :as sync-model]
   [common.teams :as teams]
   [common.timezones :as timezones]
   [ui.dom-globals :as dg]
   [common.util :as cu]
   [frontend.db :as db]
   [frontend.local-storage :as ls]
   [frontend.logging :as logging]
   [reagent.core :as r]
   [reagent.ratom :as ratom]))

(defonce pending-updates (r/atom {}))
(defonce failed-updates (r/atom {}))

(defonce session db/session)
(defonce maintenance (r/cursor session [:maintenance]))
(defonce sync-reset (r/cursor session [:sync-reset]))
(defonce reset-sync? (r/cursor sync-reset [:reset-sync?]))
(defonce sidebar-open? (r/cursor session [:sidebar-open?]))
(defonce tenant db/tenant)
(defonce tenant-schema
  (ratom/reaction
   (registry/cross-entity-schema @tenant)))
(defonce tenant-plan (r/cursor tenant [:tenant/product-plan]))
(defonce tenant-id db/tenant-id)
(defonce tenant-name (r/cursor tenant [:tenant/name]))
(defonce selected-pipelines (r/cursor session [:selected-pipelines]))
(defonce tenant-modules (r/cursor tenant [:tenant/modules]))
(defonce tenant-inbox-email (r/cursor tenant [:tenant.inbox/email]))
(defonce default-currency (r/cursor tenant [:tenant/default-currency]))
(defonce inbound-email-domain (r/cursor db/app-state [:inbound-email-domain]))
(defonce decision-configuration (r/cursor tenant [:tenant/decision-configuration]))
(defonce tenant-preferences
  (ratom/reaction
   (:tenant/preferences @tenant)))

(defonce inbound-email-address
  (ratom/reaction
   (cu/str-trim-lcase
    (str @tenant-inbox-email "@" @inbound-email-domain))))

(defonce routed-view-id (r/cursor session [:view-id]))

(defonce account (r/cursor session [:account]))
(defonce account-preferences (r/cursor account [:account/preferences]))
(defonce navbar-version-2? (r/cursor account-preferences [:navbar :version-2?]))
(defonce editable-grid? (r/cursor account-preferences [:editable-grid?]))
(defonce application-analytics? (r/cursor account-preferences [:application-analytics?]))
(defonce customizable-nev? (r/cursor account-preferences [:customizable-nev?]))

(defonce skip-indexeddb? (r/cursor account-preferences [:skip-indexeddb?]))

(defonce view-config (r/cursor account [:account/view-config]))
(defonce account-nylas-status (r/cursor account [:account/nylas-status]))
(defonce account-id (r/cursor account [:db/id]))
(defonce account-seen-pr-spinner? (r/cursor account [:account/seen-pr-spinner?]))
(defonce account-do-onboarding? (r/cursor account [:account/do-onboarding?]))
(defonce flagged-entity-ids (r/cursor account [:account/flagged-entities]))

(defonce entities db/entities)
(defonce entity-types db/entity-types)

(defonce default-vault-config
  (ratom/reaction
   (->> @tenant
        :tenant/default-vault-config
        (get @entities))))

;; reaction of entities where disabled entities have been removed.
;; use @[entities] instead of (->> @all-[entities] (remove :common/disabled?)) when applicable
(defn remove-disabled-reaction
  [entities-reaction]
  (ratom/reaction
   (->> @entities-reaction
        (remove :common/disabled?)
        doall)))

(defn as-options-reaction
  [entities-reaction*]
  (ratom/reaction
   (->> @entities-reaction*
        (sort-by :common/ord)
        (map (fn [e]
               {:value (:db/id e)
                :label (entity/display-string e)})))))

(defonce all-roles db/roles)
(defonce roles (remove-disabled-reaction all-roles))
(defonce bsg-roles
  (ratom/reaction
   (->> @all-roles
        (filter (fn [r]
                  (roles/is-bsg-role? (:role/role r))))
        (doall))))
(defonce ssg-roles
  (ratom/reaction
   (->> @all-roles
        (filter (fn [r]
                  (roles/is-ssg-role? (:role/role r))))
        (doall))))
(defonce lpg-roles
  (ratom/reaction
   (->> @all-roles
        (filter (fn [r]
                  (roles/lp-portal-guest? (:role/role r))))
        (doall))))

(defonce admin-roles
  (ratom/reaction
   (->> @all-roles
        (filter (fn [r]
                  (= :role/admin (:role/role r)))))))

(defonce all-accounts
  (ratom/reaction
   (:entity.type/account @entity-types)))

(defonce active-user-accounts-set
  (ratom/reaction
   (->> @roles
        (keep (fn [r]
                (when (some #{(:role/role r)} [:role/user :role/admin :role/zadmin :role/sys-admin])
                  (:role/subject r))))
        (set))))

(defonce admin-role-account-ids
  (ratom/reaction
   (->> @admin-roles
        (remove :common/disabled?)
        (map :role/subject)
        (remove :common/disabled?)
        (set))))

(defonce all-admins
  (ratom/reaction
   (->> @all-accounts
        (filter (fn [a]
                  (some #{(:db/id a)} @admin-role-account-ids))))))

(defonce accounts (remove-disabled-reaction all-accounts))
(defonce all-teams
  (ratom/reaction
   (:entity.type/team @entity-types)))
(defonce teams (remove-disabled-reaction all-teams))

(defonce active-teams-as-options*
  (as-options-reaction teams))

(defonce all-insider-groups
  (ratom/reaction
   (:entity.type/insider-group @entity-types)))

(defonce all-decisions
  (ratom/reaction
   (let [entity-types @entity-types]
     (->> decision-entity-types
          (mapcat (fn [entity-type]
                    (get entity-types entity-type)))
          (vec)))))

(defonce decisions
  (remove-disabled-reaction all-decisions))

(defonce all-decision-participants
  (ratom/reaction
   (:entity.type/decision-participant @entity-types)))
(defonce decision-participants
  (remove-disabled-reaction all-decision-participants))

(defonce insider-groups (remove-disabled-reaction all-insider-groups))

(defonce all-metabase-dashboards
  (ratom/reaction
   (:entity.type/metabase-dashboard @entity-types)))

(defonce metabase-dashboards
  (remove-disabled-reaction all-metabase-dashboards))

(defonce all-email-templates
  (ratom/reaction
   (:entity.type/email-template @entity-types)))
(defonce email-templates (remove-disabled-reaction all-email-templates))
(defn email-template-for
  [entity-id]
  (->> @email-templates
       (filter (fn [et]
                 (some #{entity-id} (:email-template/related-to et))))
       (first)))

(defonce account-teams
  (ratom/reaction
   (->> @all-teams
        (filter (fn [t]
                  (some #{@account-id} (:team/members t)))))))

(defonce all-pipelines
  (ratom/reaction
   (->> @entity-types
        :entity.type/pipeline
        (sort-by (fn [p]
                   (->> p
                        :pipeline/name
                        (cu/str-trim-lcase)))))))
(defonce pipelines (remove-disabled-reaction all-pipelines))
(defonce all-oppo-pipelines
  (ratom/reaction
   (->> @all-pipelines
        (cu/filter-by :pipeline/entity-type :entity.type/oppo)
        doall)))
(defonce oppo-pipelines (remove-disabled-reaction all-oppo-pipelines))
(defonce all-froppo-pipelines
  (ratom/reaction
   (->> @all-pipelines
        (cu/filter-by :pipeline/entity-type :entity.type/fundraising-opportunity)
        doall)))
(defonce froppo-pipelines (remove-disabled-reaction all-froppo-pipelines))
(defonce all-sales-oppo-pipelines
  (ratom/reaction
   (->> @all-pipelines
        (cu/filter-by :pipeline/entity-type :entity.type/sales-opportunity)
        doall)))
(defonce sales-oppo-pipelines (remove-disabled-reaction all-sales-oppo-pipelines))
(defonce all-exit-oppo-pipelines
  (ratom/reaction
   (->> @all-pipelines
        (cu/filter-by :pipeline/entity-type :entity.type/exit-opportunity)
        doall)))
(defonce exit-oppo-pipelines (remove-disabled-reaction all-exit-oppo-pipelines))
(defonce all-associations
  (ratom/reaction
   (:entity.type/entity-association @entity-types)))
(defonce associations (remove-disabled-reaction all-associations))
(defonce all-oppos
  (ratom/reaction
   (:entity.type/oppo @entity-types)))
(defonce oppos (remove-disabled-reaction all-oppos))

(defonce all-zap-vaults
  (ratom/reaction
   (:entity.type/zap-vault @entity-types)))
(defonce zap-vaults (remove-disabled-reaction all-zap-vaults))

(defonce all-companies
  (ratom/reaction
   (:entity.type/company @entity-types)))
(defonce companies (remove-disabled-reaction all-companies))

(defonce all-fof-assets
  (ratom/reaction
   (:entity.type/fof-asset @entity-types)))
(defonce fof-assets (remove-disabled-reaction all-fof-assets))
(defonce all-funds
  (ratom/reaction
   (:entity.type/fund @entity-types)))
(defonce funds (remove-disabled-reaction all-funds))
(defonce all-fund-commitments
  (ratom/reaction
   (:entity.type/fund-commitment @entity-types)))
(defonce fund-commitments (remove-disabled-reaction all-fund-commitments))
(defonce all-fundraising-opportunities
  (ratom/reaction
   (:entity.type/fundraising-opportunity @entity-types)))
(defonce fundraising-opportunities (remove-disabled-reaction all-fundraising-opportunities))
(defonce all-sales-opportunities
  (ratom/reaction
   (:entity.type/sales-opportunity @entity-types)))
(defonce sales-opportunities (remove-disabled-reaction all-sales-opportunities))
(defonce all-exit-opportunities
  (ratom/reaction
   (:entity.type/exit-opportunity @entity-types)))
(defonce exit-opportunities (remove-disabled-reaction all-exit-opportunities))
(defonce all-sale-agreements
  (ratom/reaction
   (:entity.type/sale-agreement @entity-types)))
(defonce sale-agreements (remove-disabled-reaction all-sale-agreements))
(defonce all-holdings
  (ratom/reaction
   (:entity.type/holding @entity-types)))
(defonce holdings (remove-disabled-reaction all-holdings))
(defonce investment-reservation-types
  (ratom/reaction
   (:entity.type/investment-reservation-type @entity-types)))
(defonce all-investors
  (ratom/reaction
   (:entity.type/investor @entity-types)))
(defonce investors (remove-disabled-reaction all-investors))
(defonce investor-legal-entity-types
  (ratom/reaction (->> @investors
                       (map :investor/legal-entity)
                       (keep (fn [id]
                               (->> id
                                    (get @entities)
                                    :entity/type)))
                       set)))

(defonce all-other-assets
  (ratom/reaction
   (:entity.type/other-asset @entity-types)))
(defonce other-assets (remove-disabled-reaction all-other-assets))
(defonce all-persons
  (ratom/reaction
   (:entity.type/person @entity-types)))
(defonce persons (remove-disabled-reaction all-persons))
(defonce all-real-estates
  (ratom/reaction
   (:entity.type/real-estate @entity-types)))
(defonce real-estates (remove-disabled-reaction all-real-estates))
(defonce all-tasks
  (ratom/reaction
   (:entity.type/task @entity-types)))
(defonce tasks (remove-disabled-reaction all-tasks))
(defonce all-named-views
  (ratom/reaction
   (:entity.type/named-view @entity-types)))
(defonce named-views (remove-disabled-reaction all-named-views))
(defonce all-kpi-categories
  (ratom/reaction
   (:entity.type/kpi-category @entity-types)))
(defonce kpi-categories (remove-disabled-reaction all-kpi-categories))
(defonce all-kpi-members
  (ratom/reaction
   (:entity.type/kpi-member @entity-types)))
(defonce kpi-members (remove-disabled-reaction all-kpi-members))
(defonce all-kpi-cycles
  (ratom/reaction
   (:entity.type/kpi-cycle @entity-types)))
(defonce kpi-cycles (remove-disabled-reaction all-kpi-cycles))
(defonce all-kpi-reports
  (ratom/reaction
   (:entity.type/kpi-report @entity-types)))
(defonce kpi-reports (remove-disabled-reaction all-kpi-reports))
(defonce all-kpi-report-datas
  (ratom/reaction
   (:entity.type/kpi-report-data @entity-types)))
(defonce kpi-report-datas (remove-disabled-reaction all-kpi-report-datas))
(defonce all-kpi-report-answers
  (ratom/reaction
   (:entity.type/kpi-report-answer @entity-types)))
(defonce kpi-report-answers (remove-disabled-reaction all-kpi-report-answers))

; This system level filter has been removed as per ticket DEV-6025.
; Leave as reference until new functionality is confirmed.
#_(defn- filter-kyc-profiles
    "For CapMan. A helper function for filtering kyc-profiles based on the following rule:
  If an investor has commitments AND
  all the commitments are for funds that have external kyc tool (:fund/external-kyc-tool),
  filter out the kyc profiles for these investors."
    [{:keys [kyc-profiles fund-commitments funds]}]
    (->> kyc-profiles
         (filter
          (fn [kyc-profile]
            (let [investor-id (:kyc-profile/target kyc-profile)
                  investor-commitments-fund-ids
                  (->> fund-commitments
                       (filter (fn [commitment]
                                 (= (:fund-commitment/investor commitment) investor-id)))
                       (map :fund-commitment/fund))

                  funds-with-investor-commitments
                  (->> funds
                       (filter (fn [fund]
                                 (some #{(:db/id fund)} investor-commitments-fund-ids))))
                  no-commitments? (= 0 (count funds-with-investor-commitments))
                  all-committed-funds-have-external-kyc (->> funds-with-investor-commitments
                                                             (every? (fn [fund]
                                                                       (:fund/external-kyc-tool
                                                                        fund))))]
              (or no-commitments?
                  (not all-committed-funds-have-external-kyc)))))
         (vec)
         (doall)))

(defonce all-company-counterparty-diligences
  (ratom/reaction
   (:entity.type/company-counterparty-diligence @entity-types)))

(defonce company-counterparty-diligences
  (remove-disabled-reaction all-company-counterparty-diligences))

(defonce all-person-counterparty-diligences
  (ratom/reaction
   (:entity.type/person-counterparty-diligence @entity-types)))

(defonce person-counterparty-diligences
  (remove-disabled-reaction all-person-counterparty-diligences))

(defonce all-project-counterparty-diligences
  (ratom/reaction
   (:entity.type/project-counterparty-diligence @entity-types)))

(defonce project-counterparty-diligences
  (remove-disabled-reaction all-project-counterparty-diligences))

(defonce all-transaction-processes
  (ratom/reaction
   (:entity.type/transaction-process @entity-types)))

(defonce transaction-processes
  (remove-disabled-reaction all-transaction-processes))

(defonce tenant-esign-v2?
  (ratom/reaction (:tenant/esign-v2? @tenant)))

(defonce show-esign-process-version?
  (ratom/reaction (:show-esign-process-version? @account-preferences)))

(defonce all-kyc-profiles
  (ratom/reaction
   (:entity.type/kyc-profile @entity-types)))

(defonce kyc-profiles (remove-disabled-reaction all-kyc-profiles))

(defonce all-tenant-kyc-profiles
  (ratom/reaction
   (:entity.type/tenant-kyc-profile @entity-types)))
(defonce tenant-kyc-profiles (remove-disabled-reaction all-tenant-kyc-profiles))

(defonce all-contract-kyc-profiles
  (ratom/reaction
   (:entity.type/contract-kyc-profile @entity-types)))

(defonce contract-kyc-profiles (remove-disabled-reaction all-contract-kyc-profiles))

(defonce kyc-profile-screening-statuses (r/cursor session [:kyc-profile-screening-statuses]))

(defonce all-screening-profiles
  (ratom/reaction
   (:entity.type/screening-profile @entity-types)))
(defonce all-kyc-risk-categories
  (ratom/reaction
   (:entity.type/kyc-risk-category @entity-types)))
(defonce kyc-risk-categories (remove-disabled-reaction all-kyc-risk-categories))
(defonce all-stages
  (ratom/reaction
   (:entity.type/stage @entity-types)))
(defonce stages (remove-disabled-reaction all-stages))

(defonce all-survey-types
  (ratom/reaction
   (:entity.type/survey-type @entity-types)))

(defonce survey-types (remove-disabled-reaction all-survey-types))

(defonce all-survey-templates
  (ratom/reaction
   (:entity.type/survey-template @entity-types)))

(defonce survey-templates (remove-disabled-reaction all-survey-templates))

(defonce all-survey-blocks
  (ratom/reaction
   (:entity.type/survey-block @entity-types)))

(defonce survey-blocks (remove-disabled-reaction all-survey-blocks))

(defonce all-opportunity-stages
  (ratom/reaction
   (:entity.type/opportunity-stage @entity-types)))
(defonce opportunity-stages (remove-disabled-reaction all-opportunity-stages))
(defonce all-sales-opportunity-stages
  (ratom/reaction
   (:entity.type/sales-opportunity-stage @entity-types)))
(defonce sales-opportunity-stages (remove-disabled-reaction all-sales-opportunity-stages))
(defonce all-exit-opportunity-stages
  (ratom/reaction
   (:entity.type/exit-opportunity-stage @entity-types)))
(defonce exit-opportunity-stages (remove-disabled-reaction all-exit-opportunity-stages))
(defonce all-attachments
  (ratom/reaction
   (:entity.type/attachment @entity-types)))
(defonce attachments (remove-disabled-reaction all-attachments))
(defonce all-webentries
  (ratom/reaction
   (:entity.type/webentry @entity-types)))
(defonce webentries (remove-disabled-reaction all-webentries))
(defonce all-cached-reports
  (ratom/reaction
   (:entity.type/cached-report @entity-types)))
(defonce cached-reports (remove-disabled-reaction all-cached-reports))
(defonce all-fees
  (ratom/reaction
   (:entity.type/fee @entity-types)))
(defonce fees (remove-disabled-reaction all-fees))

(defonce named-value-entities
  (ratom/reaction
   (:entity.type/named-value @entity-types)))

(defonce all-contact-lists
  (ratom/reaction
   (:entity.type/contact-list @entity-types)))

(defonce all-check-lists
  (ratom/reaction
   (:entity.type/check-list @entity-types)))

(defonce check-lists (remove-disabled-reaction all-check-lists))

(defonce contact-lists
  (remove-disabled-reaction all-contact-lists))

(def grouped-named-values
  (ratom/reaction
   (group-by :named-value/attribute @named-value-entities)))

(defonce flagged-oppos
  (ratom/reaction
   (let [account-id @account-id]
     (->> @all-oppos
          (keep (fn [o]
                  (when (some #{account-id} (:oppo/flagged-by o))
                    [(:db/id o) o])))
          (into {})))))

;;use with @(r/track named-values some-attribute) inside views to prevent unnecessary re-rendering/recalculation
(defn named-values
  [attribute]
  (get @grouped-named-values attribute))

(defn non-archived-named-values
  [attribute pipeline-id]
  (->> attribute
       (named-values)
       (remove :common/disabled?)
       (remove :common/archived?)
       (remove (fn [nv]
                 (some #{pipeline-id} (:named-value/archived-in-pipelines nv))))))

;;use with @(r/track get-entities some-entity-type) inside views to prevent unnecessary re-rendering/recalculation
(defn get-entities
  [entity-type]
  (get @entity-types entity-type))

(declare get-entity)

(defn pipeline-stages
  [pipeline-or-id]
  (let [pipeline-id (or (:db/id pipeline-or-id)
                        pipeline-or-id)
        pipeline @(r/track get-entity pipeline-id)
        et (:pipeline/entity-type pipeline)
        stage-et (pipeline/stage-entity-type et)
        stages (when stage-et
                 @(r/track get-entities stage-et))
        stage-pipeline-ref (get pipeline/entity->stage-pipeline-ref et)]
    (->> stages
         (filterv (fn [stage]
                    (= (get stage stage-pipeline-ref)
                       pipeline-id)))
         not-empty)))

(defonce sync-progress (ratom/reaction (:sync-progress @session)))

(defonce all-entities-list
  (ratom/reaction
   (doall (vals @entities))))

(defonce entities-list (remove-disabled-reaction all-entities-list))

(defonce flagged-entities                                                                           ;;NOTE oppos are concatenated,since they are using the old implementation
  (ratom/reaction
   (let [flagged @flagged-entity-ids]
     (doall
      (concat (->> @flagged-oppos
                   vals)
              (->> flagged
                   (mapv get-entity)
                   (filterv some?)))))))

;; use with @(r/track entity-reverse-relationships entity) inside views to prevent unnecessary re-rendering/recalculation
(defn entity-reverse-relationships
  [entity-or-id]
  (let [eid (or (:db/id entity-or-id)
                entity-or-id)
        reverse-ids (->> @all-entities-list
                         (filter (fn [{:keys [common/related-entities] :as _reverse-entity}]
                                   (contains? related-entities eid)))
                         (map :db/id))]
    (when (seq reverse-ids)
      (set reverse-ids))))

(defn entity-reverse-relationships-local
  [local-entities-by-id entity-or-id]
  (let [eid (or (:db/id entity-or-id)
                entity-or-id)
        reverse-ids (->> @local-entities-by-id
                         (vals)
                         (filter (fn [{:keys [common/related-entities] :as _reverse-entity}]
                                   (contains? related-entities eid)))
                         (map :db/id))]
    (when (seq reverse-ids)
      (set reverse-ids))))

;; Role keyword:
(defonce role db/role)
(defonce role-id (r/cursor session [:role-id]))
;; Role entity:
(defonce my-role
  (ratom/reaction
   (get @entities @role-id)))

(declare new-grid-2023?)

(defonce use-indexeddb?
  (ratom/reaction
   (and (some #{@role} [:role/user :role/admin])
        (not @skip-indexeddb?)
        ;; new-grid reads live from OpenSearch/Datomic rather than this
        ;; snapshot cache - keeping IndexedDB in the loop for these users only
        ;; risks a stale/corrupt local snapshot skewing the UI with no benefit.
        (not (new-grid-2023?)))))

(defonce navbar (r/cursor db/app-state [:global-navbar]))

; Todo - this will change
(defonce new-nev-in-use?
  (ratom/reaction
   (let [feature-available (or (= (:tenant/nev-2023 @tenant) :available)
                               (= (:tenant/nev-2023 @tenant) :forced))
         user-enabled-feature? (:new-nev-in-use? @view-config)]
     (and feature-available user-enabled-feature?))))
(defonce clipboard (r/cursor db/app-state [:clipboard]))
(defonce view-id (r/cursor db/app-state [:view :view-id]))
(defonce target (r/cursor session [:target]))
(defonce target-id (r/cursor target [:db/id]))
(defonce target-entity-type (r/cursor target [:entity/type]))
(defonce field-settings (r/cursor session [:field-settings]))
(defonce current-permissions (r/cursor account [:account/current-permissions]))
(defonce access-to-funds (r/cursor current-permissions [:account-access-to-funds]))
(defonce team-memberships (r/cursor current-permissions [:account-team-memberships]))
(defonce access-to-pipelines (r/cursor current-permissions
                                       [:account-access-to-pipelines]))

(defn sys-admin?
  []
  (= @role :role/sys-admin))

(defn account-is-admin?
  [acc]
  (or (sys-admin?)
      (->> @roles
           (cu/filter-by :role/subject (:db/id acc))
           (some (fn [r]
                   (some #{(:role/role r)} [:role/admin :role/zadmin :role/sys-admin]))))))

(defonce account-modules
  (ratom/reaction
   (let [account-modules (modules/resolve-account-modules
                          {:tenant         @tenant
                           :tenant-modules @tenant-modules
                           :tenant-plan    @tenant-plan
                           :all-teams      @teams
                           :user-account   @account
                           :mobile?        false
                           :owner?         (account-is-admin? @account)})]
     account-modules)))

(defonce my-tasks
  (ratom/reaction
   (let [account-id @account-id]
     (->> @tasks
          (cu/filter-by :task/assignee account-id)
          doall))))

(defonce my-unfinished-tasks
  (ratom/reaction
   (->> @my-tasks
        (remove :task/completed?)
        doall)))

;;use with @(r/track get-view-config path) in render-functions
(defn get-view-config
  [path]
  (get-in @view-config path))

(defonce
  selected-oppo-pipeline
  (ratom/reaction
   (let [current-oppo-pipeline (->> @all-oppo-pipelines
                                    (cu/filter-by :db/id (:oppo-pipeline @selected-pipelines))
                                    (first))]
     (if (or (nil? current-oppo-pipeline)
             (:pipeline/inactive? current-oppo-pipeline))
       (some->> @oppo-pipelines
                (remove :pipeline/inactive?)
                first
                :db/id)
       (:db/id current-oppo-pipeline)))))

(defonce selected-oppo-pipeline-entity
  (ratom/reaction (get @entities @selected-oppo-pipeline)))

(defonce selected-froppo-pipeline
  (ratom/reaction
   (let [current-froppo-pipeline
         (->> @all-froppo-pipelines
              (cu/filter-by :db/id (:fundraising-opportunity-pipeline @selected-pipelines))
              (first))]
     (if (or (nil? current-froppo-pipeline)
             (:pipeline/inactive? current-froppo-pipeline))
       (some->> @froppo-pipelines
                (remove :pipeline/inactive?)
                first
                :db/id)
       (:db/id current-froppo-pipeline)))))

(defonce selected-froppo-pipeline-entity
  (ratom/reaction (get @entities @selected-froppo-pipeline)))

(defonce selected-sales-oppo-pipeline
  (ratom/reaction
   (let [current-sales-pipeline
         (->> @all-sales-oppo-pipelines
              (cu/filter-by :db/id (:sales-opportunity-pipeline @selected-pipelines))
              (first))]
     (if (or (nil? current-sales-pipeline)
             (:pipeline/inactive? current-sales-pipeline))
       (some->> @sales-oppo-pipelines
                (remove :pipeline/inactive?)
                first
                :db/id)
       (:db/id current-sales-pipeline)))))

(defonce selected-sales-oppo-pipeline-entity
  (ratom/reaction (get @entities @selected-sales-oppo-pipeline)))

(defonce selected-exit-oppo-pipeline
  (ratom/reaction
   (let [current-exit-pipeline
         (->> @all-exit-oppo-pipelines
              (cu/filter-by :db/id (:exit-opportunity-pipeline @selected-pipelines))
              (first))]
     (if (or (nil? current-exit-pipeline)
             (:pipeline/inactive? current-exit-pipeline))
       (some->> @exit-oppo-pipelines
                (remove :pipeline/inactive?)
                first
                :db/id)
       (:db/id current-exit-pipeline)))))

(defonce selected-exit-oppo-pipeline-entity
  (ratom/reaction (get @entities @selected-exit-oppo-pipeline)))

(defn active-role?
  [{:keys [role/role role/target] :as role-entity}]
  (and (not (:common/disabled? role-entity))
       (or (some #{role} [:role/zadmin :role/sys-admin])
           (and (= target @tenant-id)
                (some #{(:role/role role-entity)} [:role/user :role/admin])))))

(defn active-tenant-role?
  [{:keys [role/role role/target] :as role-entity}]
  (and (not (:common/disabled? role-entity))
       (some #{role} [:role/user :role/admin])
       (= target @tenant-id)))

;; includes zadmins & sys-admins
(defn active-accounts
  [_pipeline-id]
  (some->> @all-roles
           (filter active-role?)
           (keep (fn [r]
                   (get @entities (:role/subject r))))
           (remove :common/disabled?)
           ;; TODO: @Pirinen - filter accounts that have access to pipeline if not nil ->> this has to be field specific setting
           ))

(defn active-account-id
  [account-id]
  (let [account (get @entities account-id)
        has-active-role? (some->> @all-roles
                                  (cu/filter-by :role/subject account-id)
                                  (some active-role?))]
    (when (and account
               (not (:common/disabled? account))
               has-active-role?)
      account-id)))

;; only active user and admin accounts (no guests, no zadmins/sys-admins)
(defn tenant-users
  []
  (some->> @all-roles
           (filter active-tenant-role?)
           (keep (fn [r]
                   (get @entities (:role/subject r))))
           (remove :common/disabled?)))

(defn is-guest?
  []
  (roles/is-guest-role? @role))

(defn bsg?
  []
  (= @role :role/guest-buy-side))

(defn zadmin?
  []
  (roles/zadmin? @role))

;; not used ???
(defn tenant-admin?
  []
  (roles/admin? @role))

(defn tenant-admins
  []
  (some->> @all-roles
           (filter (fn [{:keys [role/target] :as role-entity}]
                     (and (not (:common/disabled? role-entity))
                          (= (:role/role role-entity) :role/admin)
                          (= target @tenant-id))))
           (keep (fn [r]
                   (get @entities (:role/subject r))))
           (remove :common/disabled?)))

(defn user?
  []
  (roles/user? @role))

(defn is-admin?
  []
  (isa? roles/role-hierarchy @role :role/admin))

(defn admin-account?
  [account]
  (let [account-id (or (:db/id account)
                       (when-let [entity (get @entities account)]
                         (when (= :entity.type/account (:entity/type entity))
                           (:db/id entity))))]
    (or (:account/sys-admin? account)
        (->> @all-roles
             (cu/filter-by :role/subject account-id)
             (map :role/role)
             (some (fn [r]
                     (isa? roles/role-hierarchy r :role/admin)))))))

(defonce device-type (r/cursor session [:device-type]))

(defn is-mobile?
  []
  (some #{@device-type} ["ios" "android" :ios :android]))

(defn ios?
  []
  (some #{@device-type} ["ios" :ios]))

(defn activate-default-oppo-pipeline!
  []
  (when-not (:oppo-pipeline @selected-pipelines)
    (let [pipeline-id (some #{(->> @tenant
                                   :tenant/preferences
                                   :dealflow
                                   :default-pipeline)}
                            (map :db/id @all-oppo-pipelines))]
      (when pipeline-id
        (swap! selected-pipelines assoc :oppo-pipeline pipeline-id)))))

(defn activate-default-froppo-pipeline!
  []
  (when-not (:fundraising-opportunity-pipeline @selected-pipelines)
    (let [pipeline-id (some #{(->> @tenant
                                   :tenant/preferences
                                   :fundraising
                                   :default-pipeline)}
                            (map :db/id @all-froppo-pipelines))]
      (when pipeline-id
        (swap! selected-pipelines assoc :fundraising-opportunity-pipeline pipeline-id)))))

(defn activate-default-sales-oppo-pipeline!
  []
  (when-not (:sales-opportunity-pipeline @selected-pipelines)
    (let [pipeline-id (some #{(->> @tenant
                                   :tenant/preferences
                                   :sales-flow
                                   :default-pipeline)}
                            (map :db/id @all-sales-oppo-pipelines))]
      (when pipeline-id
        (swap! selected-pipelines assoc :sales-opportunity-pipeline pipeline-id)))))

(defn activate-default-exit-oppo-pipeline!
  []
  (when-not (:exit-opportunity-pipeline @selected-pipelines)
    (let [pipeline-id (some #{(->> @tenant
                                   :tenant/preferences
                                   :exit-process-management
                                   :default-pipeline)}
                            (map :db/id @all-exit-oppo-pipelines))]
      (when pipeline-id
        (swap! selected-pipelines assoc :exit-opportunity-pipeline pipeline-id)))))

(defonce ^:private last-route-synced-pipelines (atom {}))

(defn sync-selected-pipeline-from-route!
  "Applies a :pipeline-id path param onto selected-pipelines for
  pipeline-key, once per distinct id seen in the URL (tracked separately
  from selected-pipelines itself). This lets a reload or a bookmarked link
  restore the pipeline that was in the URL, without re-asserting it on every
  render while the view stays mounted - which would otherwise fight with
  other legitimate writers of selected-pipelines, e.g. new-entity creation
  defaulting to the most recently used pipeline for its type.
  Ignores ids that don't resolve against all-pipelines-for-type, so a stale
  or invalid id in the URL falls back to the normal default-pipeline
  behavior (activate-default-*-pipeline!) instead of resolving to nothing."
  [{:keys [route-params pipeline-key all-pipelines-for-type]}]
  (when-let [route-pipeline-id (some-> route-params :pipeline-id js/parseInt)]
    (when (and (not (js/isNaN route-pipeline-id))
               (not= route-pipeline-id (get @last-route-synced-pipelines pipeline-key)))
      (swap! last-route-synced-pipelines assoc pipeline-key route-pipeline-id)
      (when (cu/find-first-by :db/id route-pipeline-id @all-pipelines-for-type)
        (swap! selected-pipelines assoc pipeline-key route-pipeline-id)))))

(defn set-module!
  [module]
  (swap! session assoc :current-module module)
  (ls/set :current-module module))

(defonce tenant-data-loaded? (r/cursor session [:tenant-data-loaded?]))
(defonce account-data-loaded? (r/cursor session [:account-data-loaded?]))

(defn data-synced?
  []
  (let [tenant-data-loaded? @tenant-data-loaded?
        account-data-loaded? @account-data-loaded?]
    (and tenant-data-loaded? account-data-loaded?)))

;;use @(r/track get-entity entity-id) when applicable (inside render fns)
;; to prevent unnecessary re-rendering
(defn get-entity
  [eid]
  (or (get @entities eid)
      (when (map? eid)
        eid)))

(defn entity-in-use?
  [entity-id]
  (when-let [entity @(r/track get-entity entity-id)]
    (not (:common/disabled? entity))))

(defn reverse-lookup
  [{:keys [entity attribute-entity-types attribute]}]
  (let [entity-id (or (:db/id entity) entity)
        field-schema (registry/get-field-schema @tenant-schema attribute)
        ref-multi? (-> field-schema
                       :field/data-type
                       (= :data.type/ref-multi))
        filter-ref-multi (fn [entities]
                           (->> entities
                                (filterv
                                 (fn [candidate]
                                   (let [attr-value (get candidate attribute)]
                                     (when (set? attr-value)
                                       (contains? attr-value entity-id)))))))
        filter-ref-single (fn [entities]
                            (->> entities
                                 (cu/filter-by attribute entity-id)))
        filter-entities (if ref-multi?
                          filter-ref-multi
                          filter-ref-single)]
    (if (empty? attribute-entity-types)
      (filter-entities (vals @entities))
      (->> attribute-entity-types
           (mapcat (fn [et]
                     (filter-entities (get @entity-types et))))))))

(defn unique-company-name?
  [company-name]
  (let [lcn (->> company-name
                 (str)
                 (string/trim)
                 (string/lower-case))]
    (->> @all-companies
         (not-any? (fn [c]
                     (let [lc (->> c
                                   :asset/name
                                   (str)
                                   (string/trim)
                                   (string/lower-case))]
                       (= lc lcn)))))))

(defn long->tenant-date
  [ms]
  (let [df (or (:tenant/date-format @tenant) (loc/tr :date-format))
        tz timezones/default-timezone
        #_(or
           ;; DATE is stored in UTC 00:00 -> DO NOT USE TIMEZONE when displaying
           ;;(:tenant/timezone @tenant)
           timezones/default-timezone)]
    (date-formats/format-millis ms df tz)))

(defn long->tenant-date-time
  [ms]
  (let [df (str (or (:tenant/date-format @tenant) (loc/tr :date-format)) " HH:mm:ss")
        tz (or (:tenant/timezone @tenant) timezones/default-timezone)
        date-str (date-formats/format-millis ms df tz)]
    date-str))

(defn all-ids-found?
  [entity-ids entities-by-id]
  (->> entity-ids
       (every? (fn [id]
                 (get entities-by-id id)))))

(def ^:private session-wait-timeout-ms
  "How long the `wait-for-*` helpers below block their continuation before
  running it anyway.

  They exist to hold a modal open until the client actually has the entity, so
  the view behind it does not flash empty - cosmetic, and it used to be
  unbounded. A `nil` out of `@session/entities` reads identically whether the
  entity has not arrived yet or is never coming, and once the new grid stopped
  bulk-syncing business records (see common.sync-model) never-coming became the
  normal case: the entity creator waited on it and sat on \"Processing...\" for
  good. A deadline keeps the wait a latency optimisation instead of a liveness
  dependency."
  5000)

(defn- wait-for-session-state!
  "Calls `on-ready!` as soon as `ready?` holds for the session state, or after
  `:timeout-ms`, whichever comes first. `ready?` receives the whole session map
  so a caller can key on anything in it.

  Pass `:timeout-ms nil` for a wait whose continuation *needs* the state it is
  waiting for and would do the wrong thing if run early."
  [{:keys [ready? on-ready!] :as args}]
  (let [timeout-ms (get args :timeout-ms session-wait-timeout-ms)]
    (if (ready? @session)
      (on-ready!)
      (let [k (keyword (gensym))
            pending? (atom true)
            finish! (fn []
                      (when (compare-and-set! pending? true false)
                        (remove-watch session k)
                        (js/setTimeout on-ready! 10)))]
        (add-watch session k (fn [_k _ref _old-state new-state]
                               (when (ready? new-state)
                                 (finish!))))
        (when timeout-ms
          (js/setTimeout finish! timeout-ms))
        nil))))

(defn bulk-synced-entity-type?
  "Whether `entity-type` still reaches `@session/entities` for this account -
  see common.sync-model. Nothing outside the generic set does on the new grid,
  so nothing outside it is worth waiting for."
  [entity-type]
  (sync-model/bulk-synced-entity-type? {:new-grid?   (new-grid-2023?)
                                        :entity-type entity-type}))

(defn no-known-entities?
  "True when there is nothing of `entity-type` to choose, based on `entities`
  (the session list of that type).

  Only an account that gets the type from the bulk sync can know this. A
  new-grid account never has its business records in the session. An empty list
  there has no meaning, because the pickers search the index. The answer is
  always false there."
  [entity-type entities]
  (and (bulk-synced-entity-type? entity-type)
       (empty? entities)))

(defn wait-for-entities-sync!
  [entity-ids processing-done!]
  (wait-for-session-state!
   {:ready?    (fn [state] (all-ids-found? entity-ids (:entities state)))
    :on-ready! processing-done!}))

(defn wait-for-entity-sync!
  "Runs `close-cb!` once the newly created entity has reached the client.

  Skips the wait entirely for an entity type this account no longer bulk-syncs:
  there is nothing to wait for, and sitting out the whole deadline would just
  park the caller's spinner. Needs `:new-entity` to make that call - given only
  an id it falls back to the deadline-bounded wait."
  [{:keys [new-entity-id new-entity close-cb!]}]
  (let [entity-type (:entity/type new-entity)]
    (if (and entity-type
             (not (bulk-synced-entity-type? entity-type)))
      (close-cb!)
      (wait-for-entities-sync! [new-entity-id] close-cb!))))

(defn wait-for-entity-update!
  [entity-id previous-updated-on processing-done!]
  (wait-for-session-state!
   {:ready?    (fn [state]
                 (let [updated-on (get-in state [:entities entity-id :common/updated-on])]
                   (and (number? updated-on)
                        (> updated-on previous-updated-on))))
    :on-ready! processing-done!}))

(defn wait-for-entity-disable!
  "Note that an entity absent from `@session/entities` counts as disabled here,
  which is why this one never hung: a type that is not synced at all reads as
  already gone."
  [entity-id processing-done!]
  (wait-for-session-state!
   {:ready?    (fn [state]
                 (let [entity (get-in state [:entities entity-id])]
                   (or (not entity)
                       (:common/disabled? entity))))
    :on-ready! processing-done!}))

(defn wait-for-entity-attribute-value!
  "Deliberately has no deadline: every caller reads the attribute it waited for
  (a job's `:job/status`, then its `:job/result`) and would report a failure or
  an empty result if run before the value landed. Jobs stay in the generic sync
  set for exactly this reason - see common.sync-model."
  [entity-id attribute stopper-values-or-fn processing-done!]
  (let [reached? (fn [attr-val]
                   (if (fn? stopper-values-or-fn)
                     (stopper-values-or-fn attr-val)
                     (some #{attr-val} stopper-values-or-fn)))]
    (wait-for-session-state!
     {:ready?     (fn [state] (reached? (get-in state [:entities entity-id attribute])))
      :on-ready!  processing-done!
      :timeout-ms nil})))

(defn has-module-access?
  ([account module]
   (has-module-access? account module true))
  ([account module _check-mobile?]
   (let [has-access?
         (and (some #{module} @tenant-modules)
              (or (is-admin?)
                  (some #{module}
                        (->> account
                             :account/current-permissions
                             :account-modules))))]
     has-access?)))

(defn dealflow?
  []
  (has-module-access? @account :module/dealflow false))

(defn portfolio?
  []
  (when-not (is-guest?)
    (has-module-access? @account :module/portfolio false)))

(defn fundraising?
  []
  (when-not (is-guest?)
    (has-module-access? @account :module/fundraising false)))

(defn sales-pipelines?
  []
  (when-not (is-guest?)
    (has-module-access? @account :module/sales-pipelines false)))

(defn exit-process-management?
  []
  (when-not (is-guest?)
    (has-module-access? @account :module/exit-process-management false)))

(defn has-investors-access?
  []
  (or (fundraising?)
      (portfolio?)))

(defn holding-access?
  []
  (or (fundraising?)
      (portfolio?)))

(defn accounts-with-investor-access
  []
  (->> (active-accounts nil)
       (filter (fn [a]
                 (or (has-module-access? a :module/fundraising false)
                     (has-module-access? a :module/portfolio false))))))

(defn has-fund-access?
  [fund-or-id]
  (let [fund-id (or (:db/id fund-or-id)
                    fund-or-id)]
    (and fund-id
         (contains? @access-to-funds
                    fund-id))))

(defn has-funds-access?
  []
  (let [funds @funds]
    (and (plan/available :feature/fund-management @tenant)
         (seq funds)
         (or (sys-admin?)
             (is-admin?)
             (->> funds
                  (some has-fund-access?))))))

(defn has-feature-access?
  [feature]
  (let [avail? (and (plan/available feature @tenant)
                    (or (is-admin?)
                        (case feature
                          :feature/asset-types-other (dealflow?)
                          :feature/asset-types-fof (dealflow?)
                          :feature/asset-types-real-estate (dealflow?)
                          :feature/asset-types-company (dealflow?)
                          true)))]
    avail?))

(defn kpi-reporting?
  []
  (and (has-feature-access? :feature/kpi-reporting)
       (has-module-access? @account :module/kpi-reporting false)))

(defn limited-data-sync?
  []
  (and (has-feature-access? :feature/limited-data-sync)
       (:limited-data-sync? @account-preferences)))

(defn risks-access?
  []
  (and (not (is-guest?))
       (has-feature-access? :feature/risk-module)
       (has-module-access? @account :module/risk-module)))

(defn liabilities?
  []
  (and (not (is-guest?))
       (has-feature-access? :feature/liabilities)
       (has-module-access? @account :module/liabilities)))

(defn has-entity-access?
  [entity]
  (or (is-admin?)
      (case (:entity/type entity)
        :entity.type/pipeline (contains? @access-to-pipelines
                                         (:db/id entity))
        true)))

(defn froppo-creation-flow-feature?
  []
  (has-feature-access? :feature/new-froppo-creation))

(defn froppo-creation-flow-activated?
  []
  (let [admin? (is-admin?)
        for-me? (:froppo-creation-flow @account-preferences)
        for-all? (:froppo-creation-flow @tenant-preferences)]
    (or for-all?
        (and admin?
             for-me?))))

(defn sales-oppo-creation-flow-activated?
  []
  (let [admin? (is-admin?)
        for-me? (:sales-oppo-creation-flow @account-preferences)
        for-all? (:sales-oppo-creation-flow @tenant-preferences)]
    (or for-all?
        (and admin?
             for-me?))))

(defn deal-creation-flow-activated?
  []
  (let [admin? (is-admin?)
        for-me? (:deal-creation-flow @account-preferences)
        for-all? (:deal-creation-flow @tenant-preferences)]
    (or for-all?
        (and admin?
             for-me?))))

(defn exit-oppo-creation-flow-activated?
  []
  (let [admin? (is-admin?)
        for-me? (:exit-oppo-creation-flow @account-preferences)
        for-all? (:exit-oppo-creation-flow @tenant-preferences)]
    (or for-all?
        (and admin?
             for-me?))))

(defn froppo-creation-flow?
  []
  (and (froppo-creation-flow-feature?)
       (froppo-creation-flow-activated?)))

(defn sales-oppo-creation-flow?
  []
  (and (froppo-creation-flow-feature?)
       (sales-oppo-creation-flow-activated?)))

(defn deal-creation-flow?
  []
  (and (froppo-creation-flow-feature?)
       (deal-creation-flow-activated?)))

(defn exit-oppo-creation-flow?
  []
  (and (froppo-creation-flow-feature?)
       (exit-oppo-creation-flow-activated?)))

(defn entry-criteria-feature?
  []
  (has-feature-access? :feature/stage-entry-criteria))

(defn entry-criteria?
  "Whether Stage Entry Criteria is enabled for `pipeline-entity-type` on the
   current tenant / account. Requires the zadmin-toggleable
   `:feature/stage-entry-criteria` plan-feature, the type-specific
   creation-flow being on (entry-criteria piggybacks on the new
   opportunity-creator's resolved creator-config and is incoherent without
   it), and the type-specific tenant/account preference being flipped."
  [pipeline-entity-type]
  (let [[pref-key creation-flow?-fn]
        (case pipeline-entity-type
          :entity.type/oppo
          [:stage-entry-criteria-deal deal-creation-flow?]

          :entity.type/fundraising-opportunity
          [:stage-entry-criteria-froppo froppo-creation-flow?]

          :entity.type/sales-opportunity
          [:stage-entry-criteria-sales-oppo sales-oppo-creation-flow?]

          :entity.type/exit-opportunity
          [:stage-entry-criteria-exit-oppo exit-oppo-creation-flow?])]
    (and (entry-criteria-feature?)
         (creation-flow?-fn)
         (let [admin? (is-admin?)
               for-me? (get @account-preferences pref-key)
               for-all? (get @tenant-preferences pref-key)]
           (or for-all?
               (and admin?
                    for-me?))))))

(defn deal-entry-criteria?
  []
  (entry-criteria? :entity.type/oppo))

(defn froppo-entry-criteria?
  []
  (entry-criteria? :entity.type/fundraising-opportunity))

(defn sales-oppo-entry-criteria?
  []
  (entry-criteria? :entity.type/sales-opportunity))

(defn exit-oppo-entry-criteria?
  []
  (entry-criteria? :entity.type/exit-opportunity))

(defn custom-create-config?
  []
  (or (has-feature-access? :feature/create-config)
      (froppo-creation-flow-feature?)))

(defn decisions?
  []
  (and (not (is-guest?))
       (has-feature-access? :feature/decisions)
       (has-module-access? @account :module/decisions false)))

(defn contact-lists?
  []
  (and (not (is-guest?))
       (has-feature-access? :feature/contact-lists)
       (has-module-access? @account :module/contact-lists false)))

(defn kyc?
  []
  (and (not (is-guest?))
       (has-feature-access? :feature/kyc)
       (has-module-access? @account :module/kyc false)))

(defn tenant-kyc?
  []
  (and (not (is-guest?))
       (has-feature-access? :feature/tenant-kyc)
       (has-module-access? @account :module/tenant-kyc false)))

(defn contract-kyc?
  []
  (and (not (is-guest?))
       (has-feature-access? :feature/contract-kyc)
       (has-module-access? @account :module/contract-kyc false)))

(defn automated-kyc-renewal?
  []
  (and (kyc?)
       (has-feature-access? :feature/automated-kyc-renewal)))

(defn screening?
  []
  (and (not (is-guest?))
       (has-feature-access? :feature/screening)
       (has-module-access? @account :module/screening false)))

(defn counterparty-diligence-admin?
  []
  (and (not (is-guest?))
       (has-feature-access? :feature/counterparty-diligence)
       (has-module-access? @account :module/counterparty-diligence-admin false)))

(defn transaction-process?
  []
  (and (not (is-guest?))
       (has-feature-access? :feature/transaction-process)
       (has-module-access? @account :module/transaction-process false)))

(defn counterparty-diligence?
  []
  (and (not (is-guest?))
       (has-feature-access? :feature/counterparty-diligence)
       (has-module-access? @account :module/counterparty-diligence false)))

(defn surveys?
  []
  (and (not (is-guest?))
       (has-feature-access? :feature/surveys)
       (has-module-access? @account :module/survey false)))

(defn survey-multi-responses?
  []
  (and (surveys?)
       (has-feature-access? :feature/survey-multi-responses)))

(defn survey-skip-responses?
  []
  (and (surveys?)
       (has-feature-access? :feature/survey-skip-responses)))

(defn survey-sms-auth?
  []
  (and (surveys?)
       (has-feature-access? :feature/survey-sms-auth)))

(defn survey-excel-import?
  []
  (and (surveys?)
       (has-feature-access? :feature/survey-excel-import)))

(defn metabase-dashboards?
  []
  (and (not (is-guest?))
       (has-feature-access? :feature/metabase-dashboards)
       (has-module-access? @account :module/dashboards false)))

(defn old-dashboards?
  []
  (and (not (is-guest?))
       (has-feature-access? :feature/old-dashboards)))

(defn reporting-db?
  []
  (and (not (is-guest?))
       (has-feature-access? :feature/reporting-db)
       (has-module-access? @account :module/reporting-db false)))

(defn metabase-reporting-db?
  []
  (and (not (is-guest?))
       (has-feature-access? :feature/metabase-reporting-db)
       (has-module-access? @account :module/metabase-reporting-db false)))

(defn surveys-admin?
  []
  (and (not (is-guest?))
       (has-feature-access? :feature/surveys)
       (has-module-access? @account :module/surveys-admin false)))

(defn metabase-dashboards-admin?
  []
  (js/console.log "Metabase"
                  "\n " (not (is-guest?))
                  "\n" (has-feature-access? :feature/metabase-dashboards)
                  "\n" (has-module-access? @account :module/dashboards false)
                  "\n" (has-module-access? @account :module/dashboards-admin false))
  (and (not (is-guest?))
       (has-feature-access? :feature/metabase-dashboards)
       (has-module-access? @account :module/dashboards false)
       (has-module-access? @account :module/dashboards-admin false)))

(defn insider-group-admin?
  []
  (and (not (is-guest?))
       (has-feature-access? :feature/insider-groups)
       (has-module-access? @account :module/insider-groups-admin false)))

(defn insider-groups?
  []
  (and (not (is-guest?))
       (has-feature-access? :feature/insider-groups)
       (has-module-access? @account :module/insider-groups false)
       (->> @all-insider-groups
            (remove :common/disabled?)
            (seq))))

(defn indirect-holdings?
  []
  (and (not (is-guest?))
       (has-feature-access? :feature/indirect-holdings)
       (has-module-access? @account :module/portfolio)))

(defn value-creation?
  []
  (and (not (is-guest?))
       (has-feature-access? :feature/value-creation-activity)
       (has-module-access? @account :module/value-creation-activity false)))

(defn heap-analytics?
  []
  (and (not (is-guest?))
       (has-feature-access? :feature/application-analytics)
       (not @application-analytics?)))

(defn fee-tracking?
  []
  (and (not (is-guest?))
       (has-feature-access? :feature/fee-tracking)
       (has-module-access? @account :module/fee-tracking false)))

(defn lp-reporting?
  []
  (and (not (is-guest?))
       (has-feature-access? :feature/lp-reporting)
       (has-module-access? @account :module/lp-reporting false)))

(defn entity-graph?
  []
  (and (not (is-guest?))
       (has-feature-access? :feature/entity-graph)
       (has-module-access? @account :module/entity-graph)))

(defn zap-vault?
  []
  (and (not (is-guest?))
       (has-feature-access? :feature/zap-vault)
       (has-module-access? @account :module/zap-vault)))

(defn entity-merge?
  []
  (and (is-admin?)
       (has-feature-access? :feature/entity-merge)))

(defonce current-module-cursor (r/cursor session [:current-module]))

(defn current-module
  []
  (let [is-owner? (account-is-admin? @account)
        available-modules (set (modules/resolve-account-modules
                                {:tenant         @tenant
                                 :tenant-modules @tenant-modules
                                 :tenant-plan    @tenant-plan
                                 :all-teams      @teams
                                 :user-account   @account
                                 :mobile?        (is-mobile?)
                                 :owner?         is-owner?}))
        module-from-ls (ls/get :current-module)
        ;;make sure selected module is still available for user:
        module-from-ls (if (contains? available-modules module-from-ls)
                         module-from-ls
                         nil)
        module-from-state @current-module-cursor
        ;;make sure selected module is still available for user:
        module-from-state (if (contains? available-modules module-from-state)
                            module-from-state
                            nil)
        modules-in-order (->> (modules/all-modules @tenant @tenant-plan (is-mobile?))
                              (sort-by (fn [m]
                                         (case m
                                           :module/dealflow 0
                                           :module/fundraising 1
                                           :module/portfolio 2
                                           :module/kpi-reporting 3
                                           :module/kyc 4
                                           5))))
        module (or module-from-state
                   module-from-ls
                   (->> modules-in-order
                        (filter available-modules)
                        first))]
    (when-not module-from-state
      (set-module! module))

    module))

(defn accounts-by-module
  [module]
  (some->> @teams
           (filter (fn [t]
                     (some #{module} (:team/modules t))))
           (mapcat :team/members)
           distinct
           (keep get-entity)
           (remove :common/disabled?)
           ;;at least one active TA/TU zadmin or sys-admin role needed:
           (remove
            (fn [acc]
              (->> @all-roles
                   (cu/filter-by :role/subject (:db/id acc))
                   (remove :common/disabled?)
                   (not-any? (fn [{:keys [role/role]}]
                               (some #{role}
                                     [:role/user :role/admin :role/zadmin :role/sys-admin]))))))))

(defonce dealflow-accounts (partial accounts-by-module :module/dealflow))
(defonce fundraising-accounts (partial accounts-by-module :module/fundraising))
(defonce sales-accounts (partial accounts-by-module :module/sales-pipelines))
(defonce exit-process-accounts (partial accounts-by-module :module/exit-process-management))
(defonce portfolio-accounts (partial accounts-by-module :module/portfolio))
(defonce kpi-accounts (partial accounts-by-module :module/kpi-reporting))

(defn asset-type-feature?
  [asset-type]
  (case asset-type
    :entity.type/real-estate (has-feature-access? :feature/asset-types-real-estate)
    :entity.type/fof-asset (has-feature-access? :feature/asset-types-fof)
    :entity.type/other-asset (has-feature-access? :feature/asset-types-other)
    :entity.type/company true
    (let [type-name (loc/tr asset-type)]
      (logging/error "Could not resolve access to asset type : " type-name " >> " asset-type)
      false)))

(def available-asset-types
  (ratom/reaction
   (->> [:entity.type/company
         :entity.type/real-estate
         :entity.type/fof-asset
         :entity.type/other-asset]
        (filterv (fn [at]
                   @(r/track asset-type-feature? at))))))

(def available-asset-types-set
  (ratom/reaction
   (set @available-asset-types)))

(defn oppo-target-asset-types
  []
  (let [configured-asset-types (or (some->> @selected-oppo-pipeline
                                            (get @entities)
                                            :pipeline/target-asset-types)
                                   (some->> assets/target-asset-type-mappings
                                            :entity.type/oppo))]
    (->> configured-asset-types
         (filter asset-type-feature?))))

(defn sales-opportunity-customer-asset-types
  []
  (let [configured-asset-types (or (some->> @selected-sales-oppo-pipeline
                                            (get @entities)
                                            :pipeline/target-asset-types)
                                   (some->> assets/target-asset-type-mappings
                                            :entity.type/sales-opportunity))]
    configured-asset-types))

(defn holding-target-asset-types
  []
  (let [holding-target-types (->> @all-holdings
                                  (map :holding/target-asset)
                                  (keep get-entity)
                                  (keep :entity/type)
                                  (set)
                                  (set/union (set assets/all-asset-types)))]
    (->> holding-target-types
         (filter asset-type-feature?))))

(defn available-target-assets
  [use-deal-pipeline?]
  (->> (if use-deal-pipeline?
         (oppo-target-asset-types)
         (holding-target-asset-types))
       (sort-by loc/tr)))

(defn target-in-oppos
  [entity-id]
  (->> @oppos
       (cu/only-entity :oppo/target-asset entity-id)
       (seq)))

(defn target-in-holdings
  [entity-id]
  (->> @holdings
       (cu/only-entity :holding/target-asset entity-id)
       (seq)))

(defn target-asset-in
  [entity-id]
  (seq
   (concat (target-in-oppos entity-id)
           (target-in-holdings entity-id))))

(defn kpi-member-in
  [company-id]
  (let [kpi-members (->> @kpi-members
                         (cu/filter-by :kpi-member/asset company-id)
                         (remove (fn [member]
                                   (let [category (->> member
                                                       :kpi-member/target
                                                       (get @entities))]
                                     (or (nil? category)
                                         (:common/disabled? category)))))
                         (seq))]
    kpi-members))

(defn target-investor-in
  [investor-id]
  (let [investor-in-commitments (->> @fund-commitments
                                     (cu/filter-by :fund-commitment/investor investor-id))
        investor-in-fundraising-opportunities (->> @fundraising-opportunities
                                                   (cu/filter-by :fundraising-opportunity/investor
                                                                 investor-id))
        investor-in-sale-agreements (->> @sale-agreements
                                         (cu/filter-by :sale-agreement/customer investor-id))
        investor-in-sales-oppos (->> @sales-opportunities
                                     (cu/filter-by :sales-opportunity/customer investor-id))
        all-target-asset-entities (concat investor-in-commitments
                                          investor-in-sale-agreements
                                          investor-in-sales-oppos
                                          investor-in-fundraising-opportunities)]
    (seq all-target-asset-entities)))

(defn customer-in-sales-oppos
  [entity-id]
  (->> @sales-opportunities
       (cu/filter-by :sales-opportunity/customer entity-id)
       (seq)))

(defn customer-in-agreements
  [entity-id]
  (->> @sale-agreements
       (cu/filter-by :sale-agreement/customer entity-id)
       (seq)))

(defn buyer-candidate-in-exit-oppos
  [entity-id]
  (->> @exit-opportunities
       ;; TODO might be changed into cardinality/many:
       (cu/filter-by :exit-opportunity/buyer entity-id)
       (seq)))

(defn target-holding-in-exit-oppos
  [entity-id]
  (->> @exit-opportunities
       (cu/filter-by :exit-opportunity/target-holding entity-id)
       (seq)))

(defn investor-in-froppos
  [entity-id]
  (->> @fundraising-opportunities
       (cu/filter-by :fundraising-opportunity/investor entity-id)
       (seq)))

(defn investor-in-commitments
  [entity-id]
  (->> @fund-commitments
       (cu/filter-by :fund-commitment/investor entity-id)
       (seq)))

(defn customer-in
  [entity-id]
  (seq (concat (customer-in-sales-oppos entity-id)
               (customer-in-agreements entity-id))))

(defn fund-in-oppos
  [entity-id]
  (->> @oppos
       (cu/filter-by :oppo/fund entity-id)
       (seq)))

(defn fund-in-froppos
  [entity-id]
  (->> @fundraising-opportunities
       (cu/filter-by :fundraising-opportunity/fund entity-id)
       (seq)))

(defn fund-in-holdings
  [entity-id]
  (->> @holdings
       (cu/filter-by :holding/fund entity-id)
       (seq)))

(defn fund-in-commitments
  [entity-id]
  (->> @fund-commitments
       (cu/filter-by :fund-commitment/fund entity-id)
       (seq)))

(defn person-is-decision-participant
  [entity-id]
  (->> @decision-participants
       (cu/filter-by :decision-participant/person entity-id)
       (seq)))

(defn gp-in-funds
  [entity-id]
  (->> @fof-assets
       (remove :common/disabled?)
       (cu/filter-by :fof-asset/gp entity-id)
       (seq)))

(defn error-if-cannot-delete
  [entity-id]
  (let [entity (get-entity entity-id)]
    (case (:entity/type entity)
      :entity.type/company
      (cond (target-in-oppos entity-id) :cannot-delete-when-target-in-oppos
            (target-in-holdings entity-id) :cannot-delete-when-target-in-holdings
            (kpi-member-in entity-id) :cannot-delete-when-active-in-kpi-reports
            (customer-in-sales-oppos entity-id) :cannot-delete-when-customer-in-sales-opportunities
            (customer-in-agreements entity-id) :cannot-delete-when-customer-in-sale-agreements
            (gp-in-funds entity-id) :cannot-delete-when-gp-in-fund)
      :entity.type/fof-asset (cond (target-in-oppos entity-id) :cannot-delete-when-target-in-oppos
                                   (target-in-holdings entity-id)
                                   :cannot-delete-when-target-in-holdings)
      :entity.type/fund-commitment nil
      :entity.type/fundraising-opportunity nil
      :entity.type/fund (cond (fund-in-oppos entity-id) :cannot-delete-when-fund-in-oppos
                              (fund-in-froppos entity-id) :cannot-delete-when-fund-in-froppos
                              (fund-in-holdings entity-id) :cannot-delete-when-fund-in-holdings
                              (fund-in-commitments entity-id)
                              :cannot-delete-when-fund-in-commitments)
      :entity.type/holding
      (cond (target-holding-in-exit-oppos entity-id)
            :cannot-delete-holding-when-target-in-exit-oppo)
      :entity.type/investor
      (cond (customer-in-sales-oppos entity-id) :cannot-delete-when-customer-in-sales-opportunities
            (customer-in-agreements entity-id) :cannot-delete-when-customer-in-sale-agreements
            (buyer-candidate-in-exit-oppos entity-id) :cannot-delete-when-buyer-candidate-in-exit-opportunities
            (investor-in-froppos entity-id) :cannot-delete-when-investor-in-froppos
            (investor-in-commitments entity-id) :cannot-delete-when-investor-in-commitments)
      :entity.type/kyc-profile nil
      :entity.type/oppo nil
      :entity.type/other-asset (cond (target-in-oppos entity-id) :cannot-delete-when-target-in-oppos
                                     (target-in-holdings entity-id)
                                     :cannot-delete-when-target-in-holdings)
      :entity.type/person
      (cond (customer-in-sales-oppos entity-id) :cannot-delete-when-customer-in-sales-opportunities
            (customer-in-agreements entity-id) :cannot-delete-when-customer-in-sale-agreements
            (person-is-decision-participant entity-id) :cannot-delete-when-person-is-decision-participant)
      :entity.type/real-estate (cond (target-in-oppos entity-id) :cannot-delete-when-target-in-oppos
                                     (target-in-holdings entity-id)
                                     :cannot-delete-when-target-in-holdings)
      :entity.type/sale-agreement nil
      :entity.type/sales-opportunity nil
      :entity.type/task nil
      nil)))

(defn nylas-active?
  []
  (and (plan/available :feature/nylas-integration @tenant @role)
       (:account/nylas-account-id @account)
       (= :nylas.status/active (:account/nylas-status @account))))

(goog-define DEV false)

(defn dev?
  []
  (boolean DEV))

(defn prod?
  []
  (= @db/env :prod))

(defn access-to-pipeline
  [account pipeline]
  (let [account-id (or (:db/id account) account)
        account (get @entities account-id)
        pipeline-id (or (:db/id pipeline) pipeline)
        pipeline (get @entities pipeline-id)
        team-ids (:pipeline/teams pipeline)
        pipeline-teams (->> @teams
                            (filter (fn [t]
                                      (some #{(:db/id t)} team-ids))))
        account-teams (teams/resolve-account-teams account pipeline-teams)]
    (or (admin-account? account)
        (seq account-teams))))

(defn access-to-pipeline-with-name?
  [pipeline-name]
  (->> @pipelines
       (cu/filter-by :pipeline/name pipeline-name)
       (first)
       (access-to-pipeline @account)))

(defn active-accounts-with-pipeline-access
  [pipeline-id]
  (some->> @all-roles
           (filter active-role?)
           (map :role/subject)
           (map get-entity)
           (filter (fn [a]
                     (or (admin-account? a)                                                         ;; TODO: should admins be shown in dropdowns / ratings ?
                         (access-to-pipeline a pipeline-id))))))

(defn active-accounts-with-module-access
  [module]
  (some->> (active-accounts nil)
           (filter (fn [a]
                     (let [account-modules (modules/resolve-account-modules
                                            {:tenant         @tenant
                                             :tenant-modules @tenant-modules
                                             :tenant-plan    @tenant-plan
                                             :all-teams      (teams/resolve-account-teams a @teams)
                                             :user-account   a
                                             :mobile?        false
                                             :owner?         (account-is-admin? a)})]
                       (some #{module} account-modules))))))

(defn is-read-only-user?
  []
  (let [account-teams (teams/resolve-account-teams @account @teams)]
    (and (has-feature-access? :feature/read-only-teams)
         (->> account-teams
              (filter :team/read-only-team?)
              (seq)))))

(defn nev-v3?
  []
  (and (not (is-read-only-user?))
       (has-feature-access? :feature/nev-v3)))

(defn new-company-viewer-preview-2024?
  []
  (and (nev-v3?)
       (:new-company-viewer-preview-2024? @account-preferences)))

(defn classic-entity-viewer-enabled?
  []
  (if-not (new-company-viewer-preview-2024?)
    true
    (:classic-entity-viewer-enabled? @account-preferences)))

(defn show-experimental-ribbon-features?
  []
  (and (nev-v3?)
       (-> @view-id
           str
           (string/starts-with? ":test-ribbon"))))

(defn clear-db!
  []
  ;;TODO what else needs to be removed? :account, :tenant, :accounts?
  (swap! session dissoc :entities :account-data-loaded? :tenant-data-loaded?))

(defn tenant-class-name
  []
  (-> @tenant-inbox-email
      str
      string/lower-case))

(defn load-defaults
  []
  (activate-default-oppo-pipeline!)
  (activate-default-froppo-pipeline!)
  (activate-default-sales-oppo-pipeline!)
  (activate-default-exit-oppo-pipeline!))

#_(defn validation-entities
    [field-schema &
     {:keys [skip-archived? containing-entity entity-type]
      :or   {skip-archived? true}}]
    (let [named-value-attribute (:named-value-attribute field-schema)
          {:keys [validation-fn filter-entities-to-compare
                  fixed-entity-types]}
          (or (some->> field-schema
                       :field/constraints
                       :constraint/unique-tenant-ignore-case)
              (some->> field-schema
                       :field/constraints
                       :constraint/unique-tenant-ignore-case-additional-attribute))
          entities
          (when (or filter-entities-to-compare fixed-entity-types)
            (->> (or fixed-entity-types (keys @session/entity-types))
                 (mapcat (fn [et]
                           (->> (session/get-entities et)
                                (remove :common/disabled?)
                                (filter (fn [candidate]
                                          (or (not filter-entities-to-compare)
                                              (filter-entities-to-compare candidate
                                                                          field-schema)))))))
                 (doall)))
          entities (if-not named-value-attribute
                     entities
                     (->> entities
                          (cu/only-entity :named-value/attribute named-value-attribute)))
          pipeline-id (pipeline/entity-pipeline containing-entity)
          entities (if skip-archived?
                     (->> entities
                          (remove :common/archived?)
                          (remove
                           (fn [e]
                             (and (= :entity.type/named-value (:entity/type e))
                                  (some #{pipeline-id} (:named-value/archived-in-pipelines e))))))
                     entities)]
      entities))

(defn unique-check
  ([_skip-archived? open-duplicate entity-types-opts attribute value entity-id]
   (let [entities (->> entity-types-opts
                       (mapcat (fn [et]
                                 (get @entity-types et))))]
     (when-let [duplicate (->> entities
                               (remove (fn [e]
                                         (= (:db/id e) entity-id)))
                               (cu/filter-by (fn [e]
                                               (cu/str-trim-lcase (get e attribute)))
                                             value)
                               (first))]
       [:span
        (loc/tr :duplicate-value
            [(entity/resolve-entity-type-name duplicate) value])
        (when (fn? open-duplicate)
          [:<>
           [:span
            (loc/tr :open-duplicate-value
                [(string/lower-case (entity/resolve-entity-type-name duplicate))
                 (entity/display-string duplicate)])]
           [:a
            {:href     "#"
             :on-click (fn [e]
                         (dg/prevent-and-stop e)
                         (open-duplicate (:db/id duplicate)))}
            (loc/tr :here)]])])))
  ([_skip-archived? _open-duplicate entity-types-opts attribute value entity-id secondary-attribute
    secondary-value]
   (let [entities (->> entity-types-opts
                       (mapcat (fn [et]
                                 (get @entity-types et))))]
     (when (->> entities
                (remove (fn [e]
                          (= (:db/id e) entity-id)))
                (cu/filter-by secondary-attribute secondary-value)
                (cu/filter-by (fn [e]
                                (cu/str-trim-lcase (get e attribute)))
                              value)
                (seq))
       (loc/tr :duplicate-value
           [(loc/field-name attribute (registry/get-field-schema @tenant-schema attribute))
            value])))))

;; Return value also contains a string-only :error-message-str to use
;; with tooltips:
(defn unique-check-map
  ([_skip-archived? open-duplicate entity-types-opts attribute value entity-id]
   (let [entities (->> entity-types-opts
                       (mapcat (fn [et]
                                 (get @entity-types et))))]
     (when-let [duplicate (->> entities
                               (remove (fn [e]
                                         (= (:db/id e) entity-id)))
                               (cu/filter-by (fn [e]
                                               (cu/str-trim-lcase (get e attribute)))
                                             value)
                               (first))]
       {:error-message-str
        (loc/tr :duplicate-value
            [(entity/resolve-entity-type-name duplicate) value])
        :error-message
        [:span
         (loc/tr :duplicate-value
             [(entity/resolve-entity-type-name duplicate) value])
         (when (fn? open-duplicate)
           [:<>
            [:span
             (loc/tr :open-duplicate-value
                 [(string/lower-case (entity/resolve-entity-type-name duplicate))
                  (entity/display-string duplicate)])]
            [:a
             {:href     "#"
              :on-click (fn [e]
                          (dg/prevent-and-stop e)
                          (open-duplicate (:db/id duplicate)))}
             (loc/tr :here)]])]})))
  ([_skip-archived? _open-duplicate entity-types-opts attribute value entity-id secondary-attribute
    secondary-value]
   (let [entities (->> entity-types-opts
                       (mapcat (fn [et]
                                 (get @entity-types et))))]
     (when (->> entities
                (remove (fn [e]
                          (= (:db/id e) entity-id)))
                (cu/filter-by secondary-attribute secondary-value)
                (cu/filter-by (fn [e]
                                (cu/str-trim-lcase (get e attribute)))
                              value)
                (seq))
       {:error-message
        (loc/tr :duplicate-value
            [(loc/field-name attribute (registry/get-field-schema @tenant-schema attribute))
             value])}))))

(defn has-activity-access?
  [activity-type]
  (or (is-admin?)
      (->> @account-teams
           (some (fn [team]
                   (contains? (set (:team/accessible-activities team)) activity-type)))
           (boolean))))

(defn tasks-access?
  []
  (has-activity-access? :entity.type/task))

(defn note-activity?
  []
  (has-activity-access? :entity.type/note))

(defn call-activity?
  []
  (has-activity-access? :entity.type/note-call))

(defn meeting-activity?
  []
  (has-activity-access? :entity.type/note-meeting))

(defn message-activity?
  []
  (has-activity-access? :entity.type/note-message))

(defn email-activity?
  []
  (has-activity-access? :entity.type/email))

(defn system-activity?
  []
  (has-activity-access? :entity.type/system-activity))

(defn value-creation-can-be-created?
  [primary-entity]
  (let [primary-entity-type (:entity/type primary-entity)]
    (and (value-creation?)
         (or (= primary-entity-type :entity.type/investor)
             (and (= primary-entity-type :entity.type/holding)
                  (some->> primary-entity
                           :holding/target-asset
                           (get-entity)
                           :entity/type
                           (= :entity.type/company)))))))

(defn any-activity-access?
  []
  (or (note-activity?)
      (call-activity?)
      (meeting-activity?)
      (message-activity?)
      (email-activity?)
      (system-activity?)
      (value-creation?)))

(defn any-creatable-activity-access?
  [entity]
  (or (note-activity?)
      (call-activity?)
      (meeting-activity?)
      (message-activity?)
      (email-activity?)
      (value-creation-can-be-created? entity)))

(defn activity-has-tag-field?
  []
  (or (note-activity?)
      (call-activity?)
      (meeting-activity?)
      (message-activity?)
      (email-activity?)))

(defn event-pane-disabled?
  []
  (or (is-guest?)
      (and
       (not (tasks-access?))
       (not (any-activity-access?)))))

(defn new-grid-2023?
  []
  (and (has-feature-access? :feature/new-grid-2023)
       (:new-grid-2023? @account-preferences)))

(defn resolve-pipeline-id
  [{:keys [containing-entity]}]
  (or (some->> containing-entity :oppo/pipeline)
      (some->> containing-entity :fundraising-opportunity/pipeline)
      (some->> containing-entity :sales-opportunity/pipeline)
      (some->> containing-entity :exit-opportunity/pipeline)))

(defn local-search
  [{:keys [creation-pipeline-id selected-pipeline-id named-value-attribute attribute search-query entity-types] :as ctx}]
  (let [creation-pipeline-id (or creation-pipeline-id
                                 selected-pipeline-id
                                 (resolve-pipeline-id ctx))
        l-q (->> search-query
                 (cu/str-trim-lcase))
        q (when-not (cu/blank-value? l-q)
            l-q)]
    (->> entity-types
         (mapcat (fn [entity-type]
                   (case entity-type
                     :entity.type/pipeline
                     (->> @pipelines
                          (filter (fn [p]
                                    (= (:pipeline/entity-type p)
                                       (pipeline/pipeline-entity-type-by-attribute attribute))))
                          (filter (fn [p]
                                    (or (not q)
                                        (string/starts-with? (cu/str-trim-lcase
                                                              (:pipeline/name p))
                                                             q))))
                          (sort-by :pipeline/name))
                     :entity.type/named-value
                     (let [res (named-values named-value-attribute)]
                       (if-let [lc (when-not (cu/blank-value? search-query)
                                     (cu/str-trim-lcase search-query))]
                         (->> res
                              (filter (fn [nv]
                                        (string/starts-with? (cu/str-trim-lcase (:named-value/value nv)) lc))))
                         res))
                     :entity.type/stage
                     (let [res (->> @stages
                                    (cu/filter-by :stage/pipeline (or creation-pipeline-id
                                                                      selected-pipeline-id))
                                    (filter (fn [stage]
                                              (or (cu/blank-value? q)
                                                  (string/includes? (cu/str-trim-lcase (:stage/name stage))
                                                                    q))))
                                    (sort-by :common/ord))]
                       (when-not creation-pipeline-id
                         (js/console.error "NO PIPELINE PARAM FOR STAGE"))
                       res)
                     :entity.type/opportunity-stage
                     (let [res (->> @opportunity-stages
                                    (cu/filter-by :opportunity-stage/pipeline (or creation-pipeline-id
                                                                                  selected-pipeline-id))
                                    (filter (fn [stage]
                                              (or (cu/blank-value? q)
                                                  (string/includes? (cu/str-trim-lcase (:opportunity-stage/name stage))
                                                                    q))))
                                    (sort-by :common/ord))]
                       (when-not creation-pipeline-id
                         (js/console.error "NO PIPELINE PARAM FOR froppo STAGE"))
                       res)
                     :entity.type/exit-opportunity-stage
                     (let [res (->> @exit-opportunity-stages
                                    (cu/filter-by :exit-opportunity-stage/pipeline (or creation-pipeline-id
                                                                                       selected-pipeline-id))
                                    (filter (fn [stage]
                                              (or (cu/blank-value? q)
                                                  (string/includes? (cu/str-trim-lcase (:exit-opportunity-stage/name stage))
                                                                    q))))
                                    (sort-by :common/ord))]
                       (when-not creation-pipeline-id
                         (js/console.error "NO PIPELINE PARAM FOR exit STAGE"))
                       res)
                     :entity.type/sales-opportunity-stage
                     (let [res (->> @sales-opportunity-stages
                                    (cu/filter-by :sales-opportunity-stage/pipeline (or creation-pipeline-id
                                                                                        selected-pipeline-id))
                                    (filter (fn [stage]
                                              (or (cu/blank-value? q)
                                                  (string/includes? (cu/str-trim-lcase (:sales-opportunity-stage/name stage))
                                                                    q))))
                                    (sort-by :common/ord))]
                       (when-not creation-pipeline-id
                         (js/console.error "NO PIPELINE PARAM FOR sales STAGE"))
                       res)
                     (js/console.log "Unimplemented local search for : " entity-type " : " (type entity-type) " <<<< " (pr-str entity-type))))))))

(def claimed-legal-entity-ids
  (ratom/reaction
   (->> @investors
        (into
         #{}
         (keep :investor/legal-entity)))))

(defn available-legal-entity?
  [company-or-person-id]
  (not (contains? @claimed-legal-entity-ids
                  company-or-person-id)))
